import React, { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  PaginationState,
  SortingState,
  VisibilityState,
} from "@tanstack/table-core";
import {
  deepMerge,
  forgetSettledURLParams,
  parseJSON,
  writeURLParam,
} from "./urlSyncHelpers";

/**
 * The slice of CORETable's view state that is shareable through a URL.
 * Everything else (density, column order, column sizing) stays a personal
 * preference in localStorage.
 */
export type URLSyncedTableState = {
  sorting?: SortingState;
  pagination?: PaginationState;
  columnVisibility?: VisibilityState;
};

/** One slice of a table's URL-synced state, or absent. */
type URLSyncedTableStateSlice = URLSyncedTableState[keyof URLSyncedTableState];

/**
 * JSON with object keys sorted, so two structurally equal states always produce
 * the same string no matter which order their keys were built in. Array order is
 * preserved because it is meaningful for sorting.
 */
const canonicalJSON = (value: URLSyncedTableStateSlice): string =>
  JSON.stringify(value, (_key, val) =>
    val !== null && typeof val === "object" && !Array.isArray(val)
      ? Object.fromEntries(
          Object.entries(val as Record<string, unknown>).sort(([a], [b]) =>
            a.localeCompare(b)
          )
        )
      : val
  ) ?? "";

/**
 * Serialise only the parts of `state` that differ from `defaultState`, so a
 * table sitting on its defaults leaves the URL untouched instead of adding a
 * long param on every page load. Returns null when nothing differs.
 */
const serializeNonDefault = (
  state: URLSyncedTableState,
  defaultState: URLSyncedTableState
): string | null => {
  const isDefault = (
    value: URLSyncedTableStateSlice,
    defaultValue: URLSyncedTableStateSlice
  ) => canonicalJSON(value) === canonicalJSON(defaultValue);

  const nonDefault: URLSyncedTableState = {
    ...(state.sorting !== undefined &&
      !isDefault(state.sorting, defaultState.sorting) && {
        sorting: state.sorting,
      }),
    ...(state.pagination !== undefined &&
      !isDefault(state.pagination, defaultState.pagination) && {
        pagination: state.pagination,
      }),
    ...(state.columnVisibility !== undefined &&
      !isDefault(state.columnVisibility, defaultState.columnVisibility) && {
        columnVisibility: state.columnVisibility,
      }),
  };

  return Object.keys(nonDefault).length > 0 ? JSON.stringify(nonDefault) : null;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isSortingState = (value: unknown): value is SortingState =>
  Array.isArray(value) &&
  value.every(
    (entry) =>
      isPlainObject(entry) &&
      typeof entry.id === "string" &&
      typeof entry.desc === "boolean"
  );

const isVisibilityState = (value: unknown): value is VisibilityState =>
  isPlainObject(value) &&
  Object.values(value).every((visible) => typeof visible === "boolean");

/**
 * Largest page size the pagination control itself offers (antd's default size
 * changer options are 10/20/50/100). A hand-written param must not be able to
 * ask for more than a user can, or one link renders every row at once.
 */
const MAX_URL_PAGE_SIZE = 100;

const isPaginationState = (value: unknown): value is PaginationState =>
  isPlainObject(value) &&
  Number.isInteger(value.pageIndex) &&
  Number(value.pageIndex) >= 0 &&
  Number.isInteger(value.pageSize) &&
  Number(value.pageSize) > 0 &&
  Number(value.pageSize) <= MAX_URL_PAGE_SIZE;

/**
 * Read the param, keeping only the slices whose shape is actually valid. The
 * param is user editable and can also be stale, so a wrong value must never
 * reach the table's stores: `onHydrate` persists what it is given to
 * localStorage, where one bad value would keep breaking the table on every
 * later visit even after the param is gone.
 *
 * A slice that is a plain object is merged onto its default first, so a compact
 * param such as `{"pagination":{"pageIndex":2}}` still describes a complete
 * state instead of being thrown away.
 */
const parseURLState = (
  raw: string,
  defaultState: URLSyncedTableState
): URLSyncedTableState | undefined => {
  const parsed = parseJSON<unknown>(raw);
  if (!isPlainObject(parsed)) return undefined;

  const withDefaults = (value: unknown, defaultValue: unknown): unknown =>
    isPlainObject(value) && isPlainObject(defaultValue)
      ? deepMerge(defaultValue, value)
      : value;

  const sorting = parsed.sorting;
  const pagination = withDefaults(parsed.pagination, defaultState.pagination);
  const columnVisibility = withDefaults(
    parsed.columnVisibility,
    defaultState.columnVisibility
  );

  return {
    ...(isSortingState(sorting) && { sorting }),
    ...(isPaginationState(pagination) && { pagination }),
    ...(isVisibilityState(columnVisibility) && { columnVisibility }),
  };
};

export type UseURLSyncedTableStateProps = {
  /** Search param the state is stored in. */
  urlKey: string;
  /** The view state the table is currently rendering. */
  state: URLSyncedTableState;
  /** The state the table falls back to when nothing has been remembered. */
  defaultState: URLSyncedTableState;
  /**
   * Called with the state found in the URL, so the caller can seed its own
   * stores from a shared link. Runs on mount and again whenever the param
   * changes from outside this hook.
   */
  onHydrate: (urlState: URLSyncedTableState) => void;
};

/**
 * Keeps CORETable's shareable view state (sorting, pagination and column
 * visibility) in a single JSON search param so the current view survives a
 * copied link, a bookmark, or the /login?next= round trip that wipes
 * localStorage.
 *
 * The URL is a mirror rather than the source of truth: an existing param is
 * handed to `onHydrate` so the table's own stores take it on, and from then on
 * every change to those stores is written back to the URL. Mirroring reactively
 * means any code path that changes the state is covered — the pagination
 * control, the page reset that follows a data change, and the column and
 * sorting menus — without each one having to remember to write the URL, and it
 * re-applies the param if another hook on the page drops it while writing its
 * own.
 *
 * Writes are tracked so a param that changed without us — a back/forward step,
 * or another link to the route we are already on — is hydrated again instead of
 * being overwritten by the state we happen to be holding. A param that
 * disappears externally is still re-written from the current state, because
 * with no param the remembered preference is the source of truth (this matches
 * useURLSyncedStateBase, which falls back to its stored value the same way).
 *
 * Writes go through writeURLParam so a param another hook wrote in the same
 * commit is not dropped — see the buffer it uses for why that happens.
 *
 * This depends on a router context, so mount it only for tables that opted into
 * URL syncing (see URLSyncedTableStateSync).
 */
export const useURLSyncedTableState = ({
  urlKey,
  state,
  defaultState,
  onHydrate,
}: UseURLSyncedTableStateProps): void => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    forgetSettledURLParams(searchParams);
  }, [searchParams]);

  const raw = searchParams.get(urlKey);
  const serialized = serializeNonDefault(state, defaultState);

  // The callback and the derived state are rebuilt on every render, so they are
  // read through refs to keep the effects below keyed on the serialised value.
  const onHydrateRef = useRef(onHydrate);
  onHydrateRef.current = onHydrate;
  const defaultStateRef = useRef(defaultState);
  defaultStateRef.current = defaultState;
  const serializedRef = useRef(serialized);
  serializedRef.current = serialized;
  const rawRef = useRef(raw);
  rawRef.current = raw;

  const hasHydratedRef = useRef(false);
  const isHydratingRef = useRef(false);
  const stateAtHydrationRef = useRef<string | null>(null);
  const selfWroteURLRef = useRef(false);
  const prevRawRef = useRef(raw);

  if (prevRawRef.current !== raw) {
    prevRawRef.current = raw;
    if (selfWroteURLRef.current) {
      selfWroteURLRef.current = false;
    } else {
      // The param moved without us writing it, so it carries an intent the
      // current state does not know about. Allow hydration to run again and
      // adopt it rather than mirroring over it.
      hasHydratedRef.current = false;
    }
  }

  useEffect(() => {
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    if (rawRef.current === null) return;
    const urlState = parseURLState(rawRef.current, defaultStateRef.current);
    if (!urlState || Object.keys(urlState).length === 0) return;
    stateAtHydrationRef.current = serializedRef.current;
    isHydratingRef.current = true;
    onHydrateRef.current(urlState);
    // Values are read through refs; raw is a dependency so an externally changed
    // param is picked up after hasHydratedRef was reset above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey, raw]);

  useEffect(() => {
    if (isHydratingRef.current) {
      // Still the pre-hydration state; writing it would overwrite the param
      // being adopted. Wait for onHydrate's state change to land rather than
      // releasing after one run: this effect also re-runs when setSearchParams
      // changes identity, so a one-shot reprieve can be spent on an unrelated
      // run and the stale state ends up in the URL anyway.
      //
      // A param whose adopted state happens to equal the state already held
      // keeps the guard on, which only means a param carrying an invalid slice
      // alongside a valid one is not rewritten until something else moves. The
      // adopted state itself is correct either way.
      if (serializedRef.current === stateAtHydrationRef.current) return;
      isHydratingRef.current = false;
    }
    if (rawRef.current === serializedRef.current) return;
    selfWroteURLRef.current = true;
    writeURLParam(urlKey, serializedRef.current, setSearchParams);
    // Values are read through refs; raw is a dependency so the mirror re-runs
    // when the param changes underneath it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey, serialized, raw, setSearchParams]);
};

/**
 * Renders nothing — it exists purely so the sync above, and its dependency on a
 * router context, is mounted only for tables that opted in with a
 * `viewStateURLKey`. A table without one can still be rendered outside a
 * router, as before.
 */
export const URLSyncedTableStateSync: React.FC<UseURLSyncedTableStateProps> =
  function URLSyncedTableStateSync({ urlKey, state, defaultState, onHydrate }) {
    useURLSyncedTableState({ urlKey, state, defaultState, onHydrate });
    return null;
  };
